import {
    Box,
    Card,
    CardContent,
    IconButton,
    Rating,
    Tooltip,
    Typography,
} from "@mui/material";
import React, { useState } from "react";
import moment from "moment";
import { Story } from "../../../services/restserver-openapi";
import ImageCarouselDialog from "../../../components/presentationalComponents/ImageCarousel";
import MissingImage from "../../presentationalComponents/MissingImage";
import CommunityUserLink from "./ProfileLink";
import ReadMoreIcon from '@mui/icons-material/ReadMore';
import PersonIcon from '@mui/icons-material/Person';
import { Link as RouterLink } from 'react-router-dom';

interface StoryCardCompactProps {
    story: Story;
    isOwner: boolean;
}

export default function StoryCardCompact({ story, isOwner }: StoryCardCompactProps) {
    const [imageViewerOpen, setImageViewerOpen] = useState(false);

    return (
        <>
            <Card
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: 380,
                    mb: 3,
                    overflow: 'hidden',
                    position: 'relative',
                }}
            >
                {/* Top Bar */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        px: 2,
                        py: 1,
                        borderBottom: '1px solid #e0e0e0',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {moment(story.storyDate).format('DD.MM.YYYY')} ·{' '}
                            <CommunityUserLink communityName={story.owner ?? "Unbekannter User"} />
                        </Typography>

                        {isOwner && (
                            <Tooltip title="Meine Story">
                                <PersonIcon fontSize="inherit" color="disabled" />
                            </Tooltip>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {story.tourFk ? (
                            (isOwner || story.tourPublic) ? (
                                <Tooltip title="Zur Tour">
                                    <IconButton
                                        size="small"
                                        component={RouterLink}
                                        to={`/vanlifer/Community/Tour/${story.tourFk}`}
                                    >
                                        <ReadMoreIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            ) : (
                                <Tooltip title="Private Tour">
                                    <span>
                                        <IconButton size="small" disabled>
                                            <ReadMoreIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )
                        ) : null}
                    </Box>
                </Box>

                {/* Image preview */}
                <Box
                    sx={{
                        width: '100%',
                        height: '200px',
                        overflow: 'hidden',
                        position: 'relative',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                    onClick={() => story.storyImages?.length && setImageViewerOpen(true)}
                >
                    {(story.storyImages && story.storyImages.length > 0 && story.storyImages[0].sasDownloadLink) ? (
                        <>
                            <Box
                                component="img"
                                src={story.storyImages[0].sasDownloadLink}
                                alt="Story-Bild"
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                }}
                            />
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: 8,
                                    right: 8,
                                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                    color: 'white',
                                    borderRadius: '12px',
                                    px: 1.2,
                                    py: 0.5,
                                    fontSize: '0.75rem',
                                }}
                            >
                                {story.storyImages.length} {story.storyImages.length === 1 ? 'Bild' : 'Bilder'}
                            </Box>
                        </>
                    ) : (
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                backgroundColor: '#eeeeee',
                            }}
                        >
                            <Box sx={{ width: '100%', textAlign: 'center' }}>
                                <MissingImage text="Keine Bilder vorhanden." />
                            </Box>
                        </Box>
                    )}
                </Box>

                {/* Content */}
                <CardContent sx={{ pt: 2, pb: 2, flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 'bold',
                                fontSize: '1rem !important',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                            }}
                        >
                            {story.title || 'Ohne Titel'}
                        </Typography>
                        <Rating value={story.rating} precision={0.5} readOnly size="small" />
                    </Box>

                    {story.content && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                display: '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: 3,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'normal',
                            }}
                        >
                            {story.content}
                        </Typography>
                    )}
                </CardContent>
            </Card>

            {/* Image Carousel */}
            <ImageCarouselDialog
                open={imageViewerOpen}
                onClose={() => setImageViewerOpen(false)}
                images={story.storyImages?.map(img => img.sasDownloadLink!)}
            />
        </>
    );
}